import { createStore } from 'vuex';
import KeyCloakService from "@/security/KeycloakService";

const apiBase = '/api/v1'

export const models = Object.freeze({
    dashboard: `${apiBase}/dashboard`,
    devices: `${apiBase}/devices`,
    integrations: `${apiBase}/integrations`,
    secrets: `${apiBase}/secrets`,
    settings: `${apiBase}/settings`,
    user: `${apiBase}/users`,
    firmwares: `${apiBase}/firmwares`,
    projects: `${apiBase}/projects`,
});

const supportedLanguages = ['en','de'];

const getBrowserLanguage = () => {
    const lang = navigator.language;
    const shortLang = lang.split('-')[0];
    if (supportedLanguages.includes(shortLang)) {
        return shortLang;
    }
    return 'en';
}
  
const store = createStore({
    state: {
        // Data from API responses for different models, eg: devices, settings.
        // The API responses are stored in this map as-is. This is invoked by
        // API requests in `http`. This initialises devices: {}, settings: {}
        // etc. on state.

        ...Object.keys(models).reduce((obj, cur) => ({ ...obj, [cur]: {} }), {}),
    
        user: {
            firstName: '',
            lastName: '',
            language: getBrowserLanguage(),
        },

        // devices: {
        //     '12345': {
        //         id: '12345',
        //         name: 'My Device',
        //         model: 'ESP32',
        //         status: 'online',
        //         updatedAt: new Date(),
        //     },
        //     '67890': {
        //         id: '67890',
        //         name: 'My Device 2',
        //         model: 'ESP8266',
        //         status: 'offline',
        //         updatedAt: new Date(),
        //     },
        // },

        // Map of loading status (true, false) indicators for different model keys
        // like devices, settings etc. loading: {devices: true, settings: true ...}.
        // The Axios API global request interceptor marks a model as loading=true
        // and the response interceptor marks it as false. The model keys are being
        // pre-initialised here to fix "reactivity" issues on first loads.
        loading: Object.keys(models).reduce((obj, cur) => ({ ...obj, [cur]: false }), {}),
        // secret details cache keyed by name
        secretDetails: {} as Record<string, any>,
    },

    mutations: {
        // Set data from API responses. `model` is 'devices', 'settings' etc.
        setModel(state, { model, data }) {
            if (model === 'settings' || model === 'user') {
                state[model] = { ...state[model], ...data };
                return;
            }
            state[model] = data;
        },
    
        // Set the loading status for a model globally. When a request starts,
        // status is set to true which is used by the UI to show loaders and block
        // forms. When a response is received, the status is set to false. This is
        // invoked by API requests in `http`.
        setLoading(state, { model, status }) {
            state.loading[model] = status;
        },
        setSecretDetails(state, { name, data }) {
            state.secretDetails = { ...state.secretDetails, [name]: data };
        },
    },

    actions: {
        // Load data from API responses. `model` is 'devices', 'settings' etc.
        async loadModel({ commit, state }, { model, force = false }) {
            if (!force) {
                const existing = state[model];
                const hasData = Array.isArray(existing) ? existing.length > 0 : existing && Object.keys(existing).length > 0;
                if (hasData) return; // already loaded
            }
            commit('setLoading', { model, status: true });
            try {
                const response = await fetch(models[model], {
                    headers: { Authorization: `Bearer ${KeyCloakService.Instance.token}` },
                });
                if (!response.ok) {
                    throw new Error(`HTTP error! Status: ${response.status}`);
                }
                const data = await response.json();
                commit('setModel', { model, data: data });
            } catch (error) {
                console.error('An error occurred:', error.message);
            } finally {
                commit('setLoading', { model, status: false });
            }
        },
        async saveModel(_, { model }) {
            try {
                const response = await fetch(models[model], {
                    method: 'PUT',
                    headers: { 
                        Authorization: `Bearer ${KeyCloakService.Instance.token}`,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(this.state[model]),
                });
                if (!response.ok) {
                    throw new Error(`HTTP error! Status: ${response.status}`);
                }
            } catch (error) {
                console.error('An error occurred:', error.message);
            }
        },
        async upsertIntegration({ dispatch }, { id, body }) {
            try {
                const response = await fetch(`${models.integrations}/${id}`, {
                    method: 'PUT',
                    headers: {
                        Authorization: `Bearer ${KeyCloakService.Instance.token}`,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(body),
                });
                if (!response.ok) {
                    const errorText = await response.text();
                    throw new Error(errorText || `HTTP error! Status: ${response.status}`);
                }
                // Refresh integrations and secrets to keep usage counts accurate
                await dispatch('loadModel', { model: 'integrations', force: true })
                await dispatch('loadModel', { model: 'secrets', force: true })
            } catch (error) {
                console.error('An error occurred:', error.message);
                throw error
            }
        },
        async checkIntegrationNameExists({ state }, { name, excludeId }) {
            try {
                // Check if name exists in current integrations state
                const integrations = state.integrations || {};
                for (const [id, integration] of Object.entries(integrations)) {
                    if (excludeId && id === excludeId) {
                        continue; // Skip the integration being edited
                    }
                    if ((integration as any).name === name) {
                        return true;
                    }
                }
                return false;
            } catch (error) {
                console.error('Error checking integration name:', error.message);
                return false;
            }
        },
        async upsertSecret({ dispatch }, { name, value }) {
            const response = await fetch(`${models.secrets}/${encodeURIComponent(name)}`, {
                method: 'PUT',
                headers: {
                    Authorization: `Bearer ${KeyCloakService.Instance.token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ value })
            })
            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`)
            }
            await dispatch('loadModel', { model: 'secrets', force: true })
        },
        async createProject({ dispatch }, { name, template }) {
            const response = await fetch(models.projects, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${KeyCloakService.Instance.token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ name, template })
            })
            if (!response.ok) {
                const text = await response.text()
                throw new Error(text || `HTTP error! Status: ${response.status}`)
            }
            const data = await response.json()
            await dispatch('loadModel', { model: 'projects', force: true })
            return data?.id || data?.projectId || name
        },
        async upsertProject({ dispatch }, { id, body }) {
            const response = await fetch(`${models.projects}/${encodeURIComponent(id)}`, {
                method: 'PUT',
                headers: {
                    Authorization: `Bearer ${KeyCloakService.Instance.token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(body)
            })
            if (!response.ok) {
                const text = await response.text()
                throw new Error(text || `HTTP error! Status: ${response.status}`)
            }
            await dispatch('loadModel', { model: 'projects', force: true })
        },
        async deleteProject({ dispatch }, { id }) {
            const response = await fetch(`${models.projects}/${encodeURIComponent(id)}`, {
                method: 'DELETE',
                headers: {
                    Authorization: `Bearer ${KeyCloakService.Instance.token}`,
                }
            })
            if (!response.ok) {
                const text = await response.text()
                throw new Error(text || `HTTP error! Status: ${response.status}`)
            }
            await dispatch('loadModel', { model: 'projects', force: true })
        },
        async listProjectFiles(_ctx, { id }) {
            const response = await fetch(`${models.projects}/${encodeURIComponent(id)}/files`, {
                headers: { Authorization: `Bearer ${KeyCloakService.Instance.token}` },
            })
            if (!response.ok) {
                const text = await response.text()
                throw new Error(text || `HTTP error! Status: ${response.status}`)
            }
            return await response.json()
        },
        async readProjectFile(_ctx, { id, path }) {
            const response = await fetch(`${models.projects}/${encodeURIComponent(id)}/files/${encodeURIComponent(path)}`, {
                headers: { Authorization: `Bearer ${KeyCloakService.Instance.token}` },
            })
            if (!response.ok) {
                const text = await response.text()
                throw new Error(text || `HTTP error! Status: ${response.status}`)
            }
            return await response.text()
        },
        async writeProjectFile(_ctx, { id, path, content }) {
            const response = await fetch(`${models.projects}/${encodeURIComponent(id)}/files/${encodeURIComponent(path)}`, {
                method: 'PUT',
                headers: {
                    Authorization: `Bearer ${KeyCloakService.Instance.token}`,
                    'Content-Type': 'text/plain; charset=utf-8',
                },
                body: content
            })
            if (!response.ok) {
                const text = await response.text()
                throw new Error(text || `HTTP error! Status: ${response.status}`)
            }
        },
        // buildProject action no longer used (stream via GET in UI)
        async deleteProjectFile(_ctx, { id, path }) {
            const response = await fetch(`${models.projects}/${encodeURIComponent(id)}/files/${encodeURIComponent(path)}`, {
                method: 'DELETE',
                headers: {
                    Authorization: `Bearer ${KeyCloakService.Instance.token}`,
                }
            })
            if (!response.ok) {
                const text = await response.text()
                throw new Error(text || `HTTP error! Status: ${response.status}`)
            }
        },
        async deleteIntegration({ dispatch }, { id }) {
            const response = await fetch(`${models.integrations}/${id}`, {
                method: 'DELETE',
                headers: {
                    Authorization: `Bearer ${KeyCloakService.Instance.token}`,
                }
            })
            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`)
            }
            // refresh lists to keep usage counts accurate
            await dispatch('loadModel', { model: 'integrations', force: true })
            await dispatch('loadModel', { model: 'secrets', force: true })
        },
        async deleteSecret({ dispatch }, { name }) {
            const response = await fetch(`${models.secrets}/${encodeURIComponent(name)}`, {
                method: 'DELETE',
                headers: {
                    Authorization: `Bearer ${KeyCloakService.Instance.token}`,
                }
            })
            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`)
            }
            await dispatch('loadModel', { model: 'secrets', force: true })
        },
        async deleteFirmware({ dispatch }, { sha }) {
            const response = await fetch(`${models.firmwares}/${encodeURIComponent(sha)}`, {
                method: 'DELETE',
                headers: {
                    Authorization: `Bearer ${KeyCloakService.Instance.token}`,
                }
            })
            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`)
            }
            await dispatch('loadModel', { model: 'firmwares', force: true })
        },
        async loadSecretDetails({ commit }, { name }) {
            const response = await fetch(`${models.secrets}/${encodeURIComponent(name)}`, {
                headers: { Authorization: `Bearer ${KeyCloakService.Instance.token}` }
            })
            if (!response.ok) {
                if (response.status === 404) {
                    commit('setSecretDetails', { name, data: null })
                    return false
                }
                throw new Error(`HTTP error! Status: ${response.status}`)
            }
            const data = await response.json()
            commit('setSecretDetails', { name, data })
            return true
        },
        async updateDevice({ dispatch }, { id, name }) {
            try {
                const response = await fetch(`${models.devices}/${id}`, {
                    method: 'PUT',
                    headers: {
                        Authorization: `Bearer ${KeyCloakService.Instance.token}`,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ name }),
                });
                if (!response.ok) {
                    const text = await response.text();
                    throw new Error(text || `HTTP ${response.status}`);
                }
                // Refresh devices to get updated data
                await dispatch('loadModel', { model: 'devices', force: true });
            } catch (error) {
                console.error('An error occurred:', error.message);
                throw error;
            }
        },
        async deleteDevice({ dispatch }, { id }) {
            const response = await fetch(`${models.devices}/${encodeURIComponent(id)}`, {
                method: 'DELETE',
                headers: {
                    Authorization: `Bearer ${KeyCloakService.Instance.token}`,
                }
            })
            if (!response.ok) {
                const text = await response.text()
                throw new Error(text || `HTTP error! Status: ${response.status}`)
            }
            await dispatch('loadModel', { model: 'devices', force: true })
        }
    }
})

export default store;
